import { AccessRules } from '@/features/access-control/access-control-helpers'
import { FEATURE_FLAGS } from 'shared'

/**
 * Only allows access to super users
 */
export const SuperUserAccessControlRule = AccessRules.superUser()

export const AgentAccessControlRule = AccessRules.not(AccessRules.featureFlag(FEATURE_FLAGS.NoAgentAccess))

// Studio access is temporarily restricted to super users while the surface is hidden from end
// users. Kept as a single rule (not `SuperUserAccessControlRule`) so re-enabling the feature flag
// is a one-line revert.
export const StudioAccessControlRule = AccessRules.superUser()

/**
 * Gates the "open manager" pathway into the managed product. Only users with the
 * ManagerAccess flag see the entry point; the backend POST .../manager-agent enforces
 * the same flag, so this is visibility, not the security boundary.
 */
export const ManagerAccessControlRule = AccessRules.featureFlag(FEATURE_FLAGS.ManagerAccess)

/**
 * Gates the Google Ads channel-data & metrics dashboard — the "View channel data & metrics"
 * KPI cards and the Campaigns/Ads insights table — on the Google Ads channel. Off by default
 * (Google Ads otherwise works exactly as before); Meta's dashboard is never gated by this.
 * Enabled per-organization from PostHog so the surface can be rolled out gradually.
 */
export const GoogleAdsDashboardAccessControlRule = AccessRules.featureFlag(FEATURE_FLAGS.GoogleAdsDashboard)

/**
 * The non-superuser part of the early-access Tools gate: always on outside production, so a tool
 * needs no PostHog setup to be worked on or demoed; in production it is gated on the
 * `tools-early-access` flag and rolled out per-organization.
 *
 * Exported separately so surfaces that mark superuser-only visibility (the sidebar's SU badge) can
 * ask "would this user see the tool without superuser powers?".
 */
export const ToolsEarlyAccessBaseControlRule = AccessRules.or(
    AccessRules.environment(['development', 'staging']),
    AccessRules.featureFlag(FEATURE_FLAGS.ToolsEarlyAccess)
)

/**
 * Gates early-access Tools pages (Naming Convention Generator and Creative Performance) — both
 * their entry points and the pages themselves. Creative Intelligence has a narrower superuser rule.
 *
 * The superuser arm is unconditional: staff always keep the tools without the flag, so the sidebar
 * can surface them to superusers everywhere. Visibility only — it never gates the underlying data
 * routes, so anything a user already saved survives the flag being turned off.
 */
export const ToolsEarlyAccessControlRule = AccessRules.or(AccessRules.superUser(), ToolsEarlyAccessBaseControlRule)

/**
 * Gates the standalone onboarding wizard page (`/onboarding`) — reaching the surface, not being sent
 * to it.
 *
 * The wizard is a hidden trial surface beside the default chat-based onboarding: staff always have
 * it, it is always reachable outside production so it can be worked on and tested without PostHog
 * setup, and in production it is rolled out per-organization on the flag. Everyone the rule denies
 * keeps today's flow untouched.
 *
 * Deliberately split from the auto-routing rule below: the environment arm exists so a tester can
 * REACH `/onboarding` by direct URL on development/staging — it must never decide that a user is
 * routed there on their own.
 */
export const HiddenOnboardingWizardAccessControlRule = AccessRules.or(
    AccessRules.superUser(),
    AccessRules.environment(['development', 'staging']),
    AccessRules.featureFlag(FEATURE_FLAGS.HiddenOnboardingWizard)
)

/**
 * Gates the routing forks that lead INTO the hidden wizard — the post-auth landing and the
 * dashboard's onboarding redirect.
 *
 * The environment-free half of `HiddenOnboardingWizardAccessControlRule`: environment grants
 * direct-URL access for testing, auto-routing requires an explicit opt-in — the flag, or staff.
 * Without that split, every new user on development/staging is silently forked into the hidden
 * surface rather than today's default flow.
 */
export const HiddenOnboardingWizardAutoRouteAccessControlRule = AccessRules.or(
    AccessRules.superUser(),
    AccessRules.featureFlag(FEATURE_FLAGS.HiddenOnboardingWizard)
)

/**
 * Gates the AI-label compliance configuration surfaces — the Compliance tab on Customize
 * page and the Compliance section of the creative preview side panel.
 *
 * The superuser arm is unconditional: staff keep both surfaces with the flag off, so opting an
 * organization in never has to go through a staff-privilege grant. `or` short-circuits on the first
 * `true`, so a confirmed superuser is granted without waiting on PostHog and a flagged user without
 * waiting on Clerk.
 *
 * Visibility only — the compliance-settings, mark-upload, and label-box endpoints are not gated on
 * it, so settings a brand saved while flagged in keep applying if the flag is turned back off. The
 * per-placement label editor has its own, wider gate (`useCreativeApprovalAccess`): correcting a
 * placement's mark and configuring the brand's mark are different privileges.
 */
export const AiComplianceLabelAccessControlRule = AccessRules.or(
    AccessRules.superUser(),
    AccessRules.featureFlag(FEATURE_FLAGS.AiComplianceLabels)
)
